import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { sesion } from './api';
import { Avatar } from './ui';

/**
 * El riel de tinta — la identidad fija del 5a.
 *
 * ── POR QUÉ ES OSCURO EN LOS DOS TEMAS ──────────────────────────────────────────
 *
 * No es un tema: es la marca. El área de trabajo cambia con el modo claro/oscuro porque ahí
 * la persona lee durante horas; el riel se queda quieto. Que haya algo que NO cambie es lo
 * que hace que el producto se sienta el mismo a las 8 de la mañana y a las 11 de la noche.
 */

/**
 * ── AQUÍ SOLO DOS DE ESTOS EXISTEN ──────────────────────────────────────────────
 *
 * `Resumen`, `Verificaciones` y `Face ID` tienen pantalla. Los demás caen en "todavía no
 * construimos esta pantalla". Es a propósito y no es deuda escondida: el riel enseña el mapa
 * del producto, y un cliente que ve el mapa entiende dónde está parado. Pero cada entrada que
 * no lleva a nada gasta un poco de confianza, así que la lista no crece hasta que algo se
 * construye.
 */
const NAV: { a: string; label: string; badge?: 'revision' }[] = [
  { a: '/', label: 'Resumen' },
  { a: '/verificaciones', label: 'Verificaciones' },
  { a: '/face-id', label: 'Face ID' },
  { a: '/revision', label: 'Revisión', badge: 'revision' },
  { a: '/recetas', label: 'Recetas' },
  { a: '/reglas', label: 'Reglas' },
  { a: '/integracion', label: 'Integración' },
  { a: '/saldo', label: 'Saldo' },
];

export function Riel({
  organizacion,
  plan,
  usuario,
  rol,
  pendientes,
  esAdmin,
  children,
}: {
  organizacion: string;
  plan: string;
  usuario: string;
  rol: string;
  pendientes: number;
  esAdmin: boolean;
  children?: ReactNode;
}) {
  const navegar = useNavigate();

  return (
    <aside
      className="flex w-[274px] shrink-0 flex-col"
      style={{ background: 'var(--ink)', color: 'var(--ink-text)' }}
    >
      <div
        className="flex flex-col gap-4 px-5 pb-[18px] pt-[22px]"
        style={{ borderBottom: '1px solid var(--ink-border)' }}
      >
        <div className="flex items-center gap-[11px]">
          <div
            className="grid h-[30px] w-[30px] place-items-center rounded-lg text-[16px] font-extrabold"
            style={{ background: 'var(--accent)', color: 'var(--logo-fg)' }}
          >
            V
          </div>
          <span
            className="text-[16px] font-bold tracking-[-0.01em]"
            style={{ color: 'var(--ink-text-strong)' }}
          >
            Valídate
          </span>
        </div>

        <button
          className="flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left"
          style={{ background: 'var(--ink-hover)', border: '1px solid rgba(255,255,255,.08)' }}
        >
          <Avatar nombre={organizacion} tono="ink" />
          <span className="flex flex-col leading-[1.15]">
            <span className="text-[12.5px] font-semibold" style={{ color: 'var(--ink-text-mid)' }}>
              {organizacion}
            </span>
            <span className="text-[10.5px]" style={{ color: 'var(--ink-text-weak)' }}>
              {plan}
            </span>
          </span>
        </button>
      </div>

      <nav className="flex flex-1 flex-col gap-[3px] overflow-auto px-3.5 py-4">
        {NAV.map((n) => (
          <NavLink
            key={n.a}
            to={n.a}
            end={n.a === '/'}
            className="flex items-center gap-3 rounded-[9px] px-3 py-2.5 text-[13.5px] transition"
            style={({ isActive }) => ({
              background: isActive ? 'var(--ink-hover)' : 'transparent',
              color: isActive ? 'var(--ink-text-strong)' : 'var(--ink-text)',
              fontWeight: isActive ? 600 : 500,
              boxShadow: isActive ? 'inset 3px 0 0 var(--accent)' : 'none',
            })}
          >
            {n.label}
            {n.badge === 'revision' && pendientes > 0 ? (
              // El ÚNICO badge del panel. Si todo lleva badge, ninguno significa nada.
              <span
                className="tabular ml-auto rounded-full px-[7px] py-px text-[11px] font-bold"
                style={{ background: 'rgba(212,160,60,.2)', color: 'var(--warn)' }}
              >
                {pendientes}
              </span>
            ) : null}
          </NavLink>
        ))}

        {/*
          El admin solo aparece si el rol es ADMIN.

          Ocultarlo NO es la seguridad — la seguridad es el @Roles(ADMIN) del servidor, que
          rechaza la llamada venga de donde venga. Esto es cortesía: no ofrecerle a alguien
          una puerta que se le va a cerrar en la cara.
        */}
        {esAdmin ? (
          <>
            <div className="my-2 h-px" style={{ background: 'var(--ink-border)' }} />
            <NavLink
              to="/admin"
              className="flex items-center gap-3 rounded-[9px] px-3 py-2.5 text-[13.5px] transition"
              style={({ isActive }) => ({
                background: isActive ? 'var(--ink-hover)' : 'transparent',
                color: isActive ? 'var(--ink-text-strong)' : 'var(--ink-text)',
                fontWeight: isActive ? 600 : 500,
                boxShadow: isActive ? 'inset 3px 0 0 var(--accent)' : 'none',
              })}
            >
              Precios y márgenes
              <span
                className="ml-auto rounded px-1.5 py-px text-[10px] font-semibold"
                style={{ background: 'rgba(255,255,255,.08)', color: 'var(--ink-text-weak)' }}
              >
                interno
              </span>
            </NavLink>
          </>
        ) : null}
      </nav>

      {children}

      <div className="px-3.5 pb-4" style={{ borderTop: '1px solid var(--ink-border)' }}>
        <div className="flex items-center gap-2.5 px-2 pb-2 pt-4">
          <Avatar nombre={usuario} tono="ink" />
          <div className="flex min-w-0 flex-col leading-[1.15]">
            <span
              className="truncate text-[12.5px] font-semibold"
              style={{ color: 'var(--ink-text-mid)' }}
            >
              {usuario}
            </span>
            <span className="text-[10.5px]" style={{ color: 'var(--ink-text-weak)' }}>
              {rol}
            </span>
          </div>
        </div>
        <button
          onClick={() => {
            sesion.limpiar();
            navegar('/entrar');
          }}
          className="w-full rounded-lg px-3 py-2 text-left text-[12.5px]"
          style={{ color: 'var(--ink-text-weak)' }}
        >
          Cerrar sesión
        </button>
      </div>
    </aside>
  );
}
